import apiConfig from 'infrastructure/Config';
import RestApiService from 'infrastructure/RestApiService';
import { TokenStorage } from 'infrastructure/TokenStorage';

import SearchApiCall from 'api/SearchApiCall';
import UserApiCall from 'api/UserApiCall';
import ClientUserApiCall from 'api/ClientUserApiCall';
import RegistrationApiCall from 'api/RegistrationsApiCall';
import ClientApiCall from 'api/ClientApiCall';
import StudioApiCall from 'api/StudioApiCall';
import NotificationApiCall from 'api/NotificationApiCall';
import NotifyTemplateApiCall from 'api/NotifyTemplateApiCall';
import PaymentApiCall from 'api/PaymentApiCall';
import ReviewApiCall from 'api/ReviewApiCall';
import TeacherApiCall from 'api/TeacherApiCall';
import RatePlanApiCall from 'api/RatePlanApiCall';
import PendingAttendanceApiCall from 'api/PendingAttendanceApiCall';
import PendingClientApiCall from 'api/PendingClientApiCall';
import CommunicationApiCall from 'api/CommunicationApiCall';
import ClientRatePlanApiCall from 'api/ClientRatePlanApiCall';
import OnlineRegistrationsApiCall from 'api/OnlineRegistrationsApiCall';
import ForumApiCall from 'api/ForumApiCall';
import FunnelDictionaryApi from 'api/FunnelDictionaryApi';
import AIApiCall from 'api/AIApiCall';
import GamificationApiCall from '../api/GamificationApiCall';
import { createReduxRestHandlerMiddleware } from 'middleware/reduxRestHandlerMiddleware';
import { createReduxTimeoutMiddleware } from 'middleware/reduxTimeoutMiddleware';
import { myStudios, setStudio, removeUser } from 'actions/actions';
import { push } from 'react-router-redux';
import routes from 'library/Routes';
import ExtraFieldApiCall from '../api/ExtraFieldApiCall';
import SystemSettingsApiCall from '../api/SystemSettingsApiCall';
import HomeworkApiCall from '../api/HomeworkApiCall';
import GradeApiCall from '../api/GradeApiCall';
import ClientPhoneApiCall from '../api/ClientPhoneApiCall';
import OrganizationConfigurationApiCall from '../api/OrganizationConfigurationApiCall';
import UserSubscriptionApiCall from '../api/UserSubscriptionApiCall';
import AdminPanelApiCall from '../api/AdminPanelApiCall';
import ExportsApiCall from '../api/ExportsApiCall';
import { IRestApiService, IServiceLocator } from '../types';
import { AnyAction, Dispatch } from 'redux';
import { StoreState } from '../types/StoreTypes';
import UserInvoiceApiCall from '../api/UserInvoiceApiCall';
import IntegrationsApiCall from '../api/IntegrationsApiCall';
import PromocodeApiCall from '../api/PromocodeApiCall';

let restApiService: RestApiService = new RestApiService(apiConfig.serverUrl);

interface Studio {
  id: string;
  [key: string]: any;
}

export class serviceLocator implements IServiceLocator {
  static get searchApi() {
    return new SearchApiCall(restApiService);
  }

  static get userApi() {
    return new UserApiCall(restApiService);
  }

  static get clientUserApi() {
    return new ClientUserApiCall(restApiService);
  }

  static get gradesApi() {
    return new GradeApiCall(restApiService);
  }

  static get registrationApi() {
    return new RegistrationApiCall(restApiService);
  }

  static get clientApi() {
    return new ClientApiCall(restApiService);
  }

  static get studioApi() {
    return new StudioApiCall(restApiService);
  }

  static get notificationApi() {
    return new NotificationApiCall(restApiService);
  }

  static get notifyTemplateApi() {
    return new NotifyTemplateApiCall(restApiService);
  }

  static get paymentApi() {
    return new PaymentApiCall(restApiService);
  }

  static get pendingAttendanceApi() {
    return new PendingAttendanceApiCall(restApiService);
  }

  static get pendingClientApi() {
    return new PendingClientApiCall(restApiService);
  }

  static get teacherApi() {
    return new TeacherApiCall(restApiService);
  }

  static get reviewApi() {
    return new ReviewApiCall(restApiService);
  }

  static get communicationApi() {
    return new CommunicationApiCall(restApiService);
  }

  static get homeworkApi() {
    return new HomeworkApiCall(restApiService);
  }

  static get forumApi() {
    return new ForumApiCall(restApiService);
  }

  static get funnelDictionaryApi() {
    return new FunnelDictionaryApi(restApiService);
  }

  static get AIApi() {
    return new AIApiCall(restApiService);
  }

  static get gamificationApi() {
    return new GamificationApiCall(restApiService);
  }

  static get ratePlanApiCall() {
    return new RatePlanApiCall(restApiService);
  }

  static get clientPhoneApiCall() {
    return new ClientPhoneApiCall(restApiService);
  }

  static get organizationConfigurationApiCall() {
    return new OrganizationConfigurationApiCall(restApiService);
  }

  static get clientRatePlanApiCall() {
    return new ClientRatePlanApiCall(restApiService);
  }

  static get onlineRegistrationsApiCall() {
    return new OnlineRegistrationsApiCall(restApiService);
  }

  static get extraFieldApiCall() {
    return new ExtraFieldApiCall(restApiService);
  }

  static get systemSettingsApiCall() {
    return new SystemSettingsApiCall(restApiService);
  }

  static get userSubscriptionApiCall() {
    return new UserSubscriptionApiCall(restApiService);
  }

  static get adminPanelApiCall() {
    return new AdminPanelApiCall(restApiService);
  }

  static get exportsApi() {
    return new ExportsApiCall(restApiService);
  }

  static get userInvoiceApiCall() {
    return new UserInvoiceApiCall(restApiService);
  }

  static get integrationsApiCall() {
    return new IntegrationsApiCall(restApiService);
  }

  static get promocodeApiCall() {
    return new PromocodeApiCall(restApiService);
  }

  static async checkDomain(host: string): Promise<any> {
    try {
      return restApiService.get<any>('domains/' + host);
    } catch (err) {
      console.log(`ServiceLocator.checkDomain: ${err}`);
      throw err;
    }
  }

  static async checkUser(): Promise<any | undefined> {
    if (!TokenStorage.getToken()) return;

    try {
      const data = await restApiService.get<{ token?: string }>('users');
      if (data.token) TokenStorage.updateAccessToken(data.token);
      return data;
    } catch (err: any) {
      const status = err?.status;
      // Only kill the session for genuine auth failures, not transient errors (network, 5xx)
      if (status === 401 || status === 403) {
        serviceLocator.userApi.killSession();
        if (status !== 403) {
          console.log(`ServiceLocator.checkUser: ${err}`);
          throw err;
        }
      } else {
        console.log(`ServiceLocator.checkUser transient error (${status}): ${err}`);
        throw err;
      }
    }
  }

  static async myStudios(): Promise<any> {
    try {
      return restApiService.get<any>('studios/my');
    } catch (err) {
      console.log(`ServiceLocator.myStudios: ${err}`);
      throw err;
    }
  }

  static async initBizUser(dispatch: Dispatch<AnyAction>): Promise<Studio[] | undefined> {
    const selectedStudioId = localStorage.getItem('selectedStudioId');

    let studio;
    if (selectedStudioId) {
      try {
        studio = await serviceLocator.getMyStudio(dispatch, selectedStudioId);
      } catch (err) {
        console.log(`Can't find studio ${selectedStudioId}`);
        console.log(`ServiceLocator.initBizUser: ${err}`);
      }
    }

    return serviceLocator.loadMyStudios(dispatch, studio);
  }

  static async loadMyStudios(dispatch: Dispatch<AnyAction>, theStudio?: Studio): Promise<Studio[] | undefined> {
    const data = await serviceLocator.myStudios();
    if (!data) {
      return;
    }

    dispatch(myStudios(data.items));
    if (theStudio) {
      return;
    }

    if (data.items.length > 0) {
      let theStudio = data.items[0];
      localStorage.setItem('selectedStudioId', theStudio.id);
      dispatch(setStudio(theStudio));
    }
    return data.items;
  }

  static async getMyStudio(dispatch: Dispatch<AnyAction>, id: string): Promise<Studio> {
    try {
      const studio = await restApiService.get<Studio>('studios/my/' + id);
      localStorage.setItem('selectedStudioId', studio.id);
      dispatch(setStudio(studio));
      return studio;
    } catch (err) {
      console.log(`ServiceLocator.getMyStudio: ${err}`);
      dispatch(push(`${routes.myStudios()}`));
      throw err;
    }
  }

  static async getMyStudioWithSchedule(
    dispatch: Dispatch<AnyAction>,
    id: string,
    attendanceView = false
  ): Promise<Studio> {
    try {
      const studio = await restApiService.get<Studio>(
        `studios/${id}/schedule${attendanceView ? '?view=attendance' : ''}`
      );
      localStorage.setItem('selectedStudioId', studio.id);
      dispatch(setStudio(studio));
      return studio;
    } catch (err) {
      console.log(`ServiceLocator.getMyStudioWithSchedule: ${err}`);
      dispatch(push(`${routes.myStudios()}`));
      throw err;
    }
  }

  static async checkClientUser(isGelios?: boolean): Promise<any | undefined> {
    if (!TokenStorage.getToken()) return;

    try {
      return restApiService.get<any>(isGelios ? `client-users?isGelios=${isGelios}` : 'client-users');
    } catch (err) {
      console.log(`ServiceLocator.checkClientUser: ${err}`);
    }
  }
}

export let initRestApi = (dispatch: Dispatch<AnyAction>, state: StoreState): void => {
  restApiService = new RestApiService(
    apiConfig.serverUrl,
    new createReduxRestHandlerMiddleware(dispatch),
    new createReduxTimeoutMiddleware(dispatch, state),
    () => {
      TokenStorage.clearSession();
      dispatch(removeUser());
    }
  );
};
